import {
  CompanyDocumentViewerDialog,
  useCompanyDocumentViewer
} from 'src/workspaces/CompanyDocumentViewer';
import { reviewCompanyDocument } from 'src/queries/tenant/companyDocumentReview';
import {
  useWorkerProfile,
  legacyProfileResponse
} from 'src/workspaces/workerProfile/WorkerProfileContext';
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import axios from 'axios';

import {
  getContractorDocuments,
  updateContractorDocumentStatus
} from 'src/helper/backend_helper';
import { CONTRACTOR_DOCUMENT_FILE } from 'src/helper/url_helpter';

import DocumentReviewDialog, { ReviewMode } from './DocumentReviewDialog';
import {
  ContractorDocument,
  DOCUMENT_SOURCE_LABELS,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_ORDER,
  STATUS_BADGE_COLORS,
  DOCUMENT_STATUS_LABELS,
  COMPANY_ACCEPTANCE_LABELS
} from './types';
import {
  resolveDocumentPreviewMime,
  withDocumentPreviewMime
} from './documentPreview';
import { getCompanyRequirementWorkforce } from 'src/queries/companyProfile/api';
import { taxFormComplianceLabel } from './taxFormCompliance';

interface ContractorDocumentsProps {
  /**
   * Optional contractor id override. When omitted the component reads
   * `location.state.user.id`, mirroring how the rest of
   * `ConstructorDetail` resolves the active contractor.
   */
  contractorId?: number;
  reviewOnly?: boolean;
}

interface ReviewState {
  mode: ReviewMode;
  document: ContractorDocument;
}

/**
 * Documents tab on the admin contractor profile (#305).
 *
 * Pulls `GET /contractors/:id/documents` and groups rows by document type.
 * For each row offers Approve / Reject / Request re-upload — all three
 * route through `PATCH /contractors/:id/documents/:docId/status`. Rejected
 * rows surface their rejection reason inline so the admin can see prior
 * feedback at a glance.
 *
 * The "View" action streams the file via the auth-gated GET endpoint and
 * opens it in a new tab through a one-shot Blob URL — same pattern as
 * `InvoiceReceiptsSection` so we never expose the upload directory directly.
 */
export default function ContractorDocuments({
  contractorId: overrideContractorId,
  reviewOnly = false
}: ContractorDocumentsProps) {
  const scope = useWorkerProfile();
  const location = useLocation();
  const stateContractorId = (
    location.state as { user?: { id?: number } } | null
  )?.user?.id;
  const contractorId =
    scope?.workerId ?? overrideContractorId ?? stateContractorId;

  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => [
      'contractor-documents',
      scope?.companyId,
      scope?.revision,
      contractorId
    ],
    [scope?.companyId, scope?.revision, contractorId]
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      scope
        ? legacyProfileResponse(scope.data.documents)
        : getContractorDocuments(contractorId as number),
    enabled: typeof contractorId === 'number' && contractorId > 0,
    select: (response: { data?: { data?: ContractorDocument[] } }) =>
      response?.data?.data ?? []
  });

  const [review, setReview] = useState<ReviewState | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const taxRequirements = useQuery({
    queryKey: ['company-worker-tax-requirements', scope?.companyId, contractorId],
    queryFn: () => getCompanyRequirementWorkforce(scope!.workspace!, scope!.companyId, 'tax-forms'),
    enabled: Boolean(scope?.workspace && contractorId),
    retry: false
  });

  const mutation = useMutation({
    mutationFn: async ({
      docId,
      payload
    }: {
      docId: number;
      payload: {
        status: 'APPROVED' | 'REJECTED';
        rejectionReason?: string;
        expiresAt?: string | null;
      };
    }) =>
      scope
        ? reviewCompanyDocument(
            scope.companyId,
            contractorId as number,
            docId,
            {
              ...payload,
              expectedRevision: review?.document.reviewRevision ?? 0
            }
          )
        : updateContractorDocumentStatus(
            contractorId as number,
            docId,
            payload
          ),
    onSuccess: (_, { payload }) => {
      void queryClient.invalidateQueries({ queryKey });
      if (scope)
        void queryClient.invalidateQueries({
          queryKey: ['company-worker-profile', scope.basePath]
        });
      if (scope) {
        void queryClient.invalidateQueries({queryKey:['company-application-evidence',String(scope.companyId)]});
        void queryClient.invalidateQueries({queryKey:['company-profile-summary']});
      }
      toast.success(
        payload.status === 'APPROVED'
          ? scope ? 'Company acceptance recorded.' : 'Document approved.'
          : scope
          ? 'Company rejection recorded. The worker can see the reason in Documents.'
          : 'Document rejected — contractor will be notified.'
      );
      setReview(null);
      setSubmitError(null);
    },
    onError: (err: unknown) => {
      const message =
        (
          err as {
            response?: { data?: { message?: string; detail?: string } };
          }
        )?.response?.data?.detail ||
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        (err instanceof Error ? err.message : 'Could not save the review.');
      setSubmitError(message);
    }
  });

  if (!contractorId) {
    return (
      <Card>
        <CardContent>
          <Alert severity="warning">
            Contractor id missing from navigation state.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const docs = (data ?? []) as ContractorDocument[];
  const currentStatusFor = (type: string) => {
    const current = docs.find(
      (doc) => doc.type === type && doc.status !== 'REPLACED'
    );
    if (!current) return 'MISSING';
    if (
      current.status === 'APPROVED' &&
      current.expiresAt &&
      new Date(current.expiresAt).getTime() <= Date.now()
    ) {
      return 'EXPIRED';
    }
    return current.status === 'PENDING' ? 'PENDING_REVIEW' : current.status;
  };
  const identityVerified = ['GOV_ID_FRONT', 'GOV_ID_BACK', 'SELFIE']
    .map(currentStatusFor)
    .every((status) => status === 'APPROVED');
  const requiredTaxTypes = scope
    ? !taxRequirements.data?.requiredTypes
      ? undefined
      : taxRequirements.data.requiredTypes.length === 0
      ? []
      : taxRequirements.data.items
          .find((worker) => worker.id === contractorId)
          ?.requiredTypes.filter((type) => ['W9', 'I9', 'W4'].includes(type))
    : ['W9', 'I9'];
  const complianceLabel = scope && taxRequirements.isLoading
    ? 'Checking requirements…'
    : scope && (taxRequirements.isError || !requiredTaxTypes)
    ? 'Status unavailable'
    : taxFormComplianceLabel(docs, requiredTaxTypes || []);

  const docsByType = useMemo(() => {
    const map = new Map<string, ContractorDocument[]>();
    for (const type of DOCUMENT_TYPE_ORDER) {
      map.set(type, []);
    }
    for (const doc of docs) {
      if (reviewOnly && (doc.supersededAt || !['PENDING', 'PENDING_REVIEW'].includes(doc.status))) continue;
      const list = map.get(doc.type);
      if (list) list.push(doc);
    }
    return map;
  }, [docs, reviewOnly]);

  const handleAction = (mode: ReviewMode, doc: ContractorDocument) => {
    setSubmitError(null);
    setReview({ mode, document: doc });
  };

  const handleSubmitReview = (action: {
    status: 'APPROVED' | 'REJECTED';
    rejectionReason?: string;
    expiresAt?: string | null;
  }) => {
    if (!review) return;
    setSubmitError(null);
    mutation.mutate({ docId: review.document.id, payload: action });
  };

  const fileViewer = useCompanyDocumentViewer();
  const handleViewFile = async (doc: ContractorDocument) => {
    await fileViewer.openDocument({
      key: String(doc.id),
      title: DOCUMENT_TYPE_LABELS[doc.type],
      fileName: doc.originalFilename || doc.filename,
      mimeType: doc.mimeType,
      load: async () => {
        const response = await axios.get(
          scope
            ? `${scope.basePath}/documents/${doc.id}/file`
            : CONTRACTOR_DOCUMENT_FILE(contractorId, doc.id),
          { responseType: 'blob' }
        );
        const blob = response.data as Blob;
        return withDocumentPreviewMime(
          blob,
          resolveDocumentPreviewMime({
            filename: doc.originalFilename || doc.filename,
            declaredMimeType: doc.mimeType,
            blobMimeType: blob.type,
            responseContentType: response.headers?.['content-type']
          })
        );
      }
    });
  };

  return (
    <Box data-testid={reviewOnly ? "worker-items-needing-review" : "contractor-documents-section"} sx={reviewOnly ? { p: 2, background: '#fff9eb', borderBottom: '1px solid #e8dcc2' } : undefined}>
      <CompanyDocumentViewerDialog
        state={fileViewer.state}
        onClose={fileViewer.closeDocument}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" data-testid="contractor-documents-heading">
          {reviewOnly ? 'Items needing review' : 'Documents'}
        </Typography>
        {!reviewOnly && <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`Tax forms: ${complianceLabel}`}
            color={
              ['Booking ready', 'No tax forms required'].includes(complianceLabel)
                ? 'success'
                : complianceLabel === 'Bid ready · review required'
                ? 'warning'
                : 'error'
            }
            data-testid="contractor-compliance-status"
          />
          <Chip
            label={identityVerified ? 'Identity verified' : 'Identity optional'}
            color={identityVerified ? 'success' : 'default'}
            variant="outlined"
            data-testid="contractor-identity-status"
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={() => void refetch()}
                disabled={isLoading}
                data-testid="contractor-documents-refresh"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>}
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          Could not load documents:{' '}
          {(error as Error | undefined)?.message ?? 'unknown error'}
        </Alert>
      ) : scope ? (
        <div className="worker-document-table-wrap">
          <table className="worker-document-table" aria-label={reviewOnly ? 'Documents needing review' : 'Available documents'}>
            <thead><tr><th>Document type</th><th>Document / status / dates</th><th>Actions</th></tr></thead>
            <tbody>
              {reviewOnly && Array.from(docsByType.values()).every(list => list.length === 0) && <tr><td colSpan={3}>No documents currently need review.</td></tr>}
              {DOCUMENT_TYPE_ORDER.filter(type => !reviewOnly || Boolean(docsByType.get(type)?.length)).map(type => {
                const list = docsByType.get(type) ?? [];
                return list.length ? list.map(doc => (
                  <DocumentRow key={doc.id} tableRow
                    doc={doc}
                    reviewDisabled={Boolean(scope.data.user.isBlocked)}
                    reviewHref={`/companies/${scope.companyId}/compliance?documentId=${doc.id}`}
                    onApprove={() => handleAction('approve', doc)}
                    onReject={() => handleAction('reject', doc)}
                    onRequestReupload={() => handleAction('request-reupload', doc)}
                    onView={() => void handleViewFile(doc)} />
                )) : <tr key={type} data-testid={`contractor-documents-group-${type}`}><td>{DOCUMENT_TYPE_LABELS[type]}</td><td>No document uploaded yet.</td><td>—</td></tr>;
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Stack spacing={2}>
          {reviewOnly && Array.from(docsByType.values()).every(list => list.length === 0) && <Typography variant="body2">No documents currently need review.</Typography>}
          {DOCUMENT_TYPE_ORDER.filter(type => !reviewOnly || Boolean(docsByType.get(type)?.length)).map((type) => {
            const list = docsByType.get(type) ?? [];
            return (
              <Card
                key={type}
                variant="outlined"
                data-testid={`contractor-documents-group-${type}`}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    {DOCUMENT_TYPE_LABELS[type]}
                  </Typography>
                  {list.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No document uploaded yet.
                    </Typography>
                  ) : (
                    <Stack divider={<Divider flexItem />} spacing={1}>
                      {list.map((doc) => (
                        <DocumentRow
                          key={doc.id}
                          doc={doc}
                          reviewDisabled={Boolean(scope?.data.user.isBlocked)}
                          reviewHref={scope ? `/companies/${scope.companyId}/compliance?documentId=${doc.id}` : undefined}
                          onApprove={() => handleAction('approve', doc)}
                          onReject={() => handleAction('reject', doc)}
                          onRequestReupload={() =>
                            handleAction('request-reupload', doc)
                          }
                          onView={() => void handleViewFile(doc)}
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <DocumentReviewDialog
        companyScoped={Boolean(scope)}
        open={Boolean(review)}
        mode={review?.mode ?? 'approve'}
        document={review?.document ?? null}
        submitting={mutation.isLoading}
        error={submitError}
        onClose={() => {
          if (mutation.isLoading) return;
          setReview(null);
          setSubmitError(null);
        }}
        onSubmit={handleSubmitReview}
      />
    </Box>
  );
}

interface DocumentRowProps {
  reviewDisabled?: boolean;
  reviewHref?: string;
  tableRow?: boolean;
  doc: ContractorDocument;
  onApprove: () => void;
  onReject: () => void;
  onRequestReupload: () => void;
  onView: () => void;
}

function DocumentRow({
  tableRow = false,
  reviewDisabled = false,
  reviewHref,
  doc,
  onApprove,
  onReject,
  onRequestReupload,
  onView
}: DocumentRowProps) {
  const isLive = doc.status !== 'REPLACED';
  const uploaded = new Date(doc.uploadedAt).toLocaleString();
  const reviewed = doc.reviewedAt
    ? new Date(doc.reviewedAt).toLocaleString()
    : null;
  const filenameLabel = doc.originalFilename || doc.filename;
  const source = doc.source ?? doc.documentSource ?? 'ATTACHED_FILE';

  const details = (<Box sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 360
            }}
            title={filenameLabel}
          >
            {filenameLabel}
          </Typography>
          <Chip
            size="small"
            label={DOCUMENT_STATUS_LABELS[doc.status]}
            color={STATUS_BADGE_COLORS[doc.status]}
            data-testid={`contractor-document-status-${doc.id}`}
          />
          {source === 'IN_APP_FORM' || source === 'FILLED_IN_APP' ? (
            <Chip
              size="small"
              label={DOCUMENT_SOURCE_LABELS[source]}
              variant="outlined"
              data-testid={`contractor-document-source-${doc.id}`}
            />
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Uploaded {uploaded}
          {reviewed ? ` · Reviewed ${reviewed}` : ''}
        </Typography>
        {doc.expiresAt ? (
          <Typography
            variant="caption"
            color={
              new Date(doc.expiresAt).getTime() <= Date.now()
                ? 'error'
                : 'text.secondary'
            }
            sx={{ display: 'block' }}
          >
            Expires {new Date(doc.expiresAt).toLocaleDateString()}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            No expiration
          </Typography>
        )}
        {doc.companyAcceptance && (
          <Typography variant="caption">
            {COMPANY_ACCEPTANCE_LABELS[doc.companyAcceptance] ?? doc.companyAcceptance}
          </Typography>
        )}
        {doc.documentSubtype ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
            data-testid={`contractor-document-subtype-${doc.id}`}
          >
            Type: {doc.documentSubtype}
          </Typography>
        ) : null}
        {doc.status === 'REJECTED' && doc.rejectionReason ? (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
            data-testid={`contractor-document-rejection-${doc.id}`}
          >
            Reason: {doc.rejectionReason}
          </Typography>
        ) : null}
      </Box>);
  const actions = (<Stack direction="row" spacing={1} flexWrap="wrap">
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={onView}
          data-testid={`contractor-document-view-${doc.id}`}
        >
          View
        </Button>
        {isLive && reviewHref ? (
          <Button
            component={Link}
            to={reviewHref}
            size="small"
            variant="outlined"
            disabled={reviewDisabled}
            data-testid={`contractor-document-review-${doc.id}`}
          >
            Review in queue
          </Button>
        ) : isLive ? (
          <>
            {doc.status !== 'APPROVED' ? (
              <Button
                size="small"
                color="success"
                variant="outlined"
                startIcon={<CheckCircleIcon />}
                disabled={reviewDisabled}
                title={
                  reviewDisabled
                    ? 'Document review is unavailable for this blocked worker'
                    : undefined
                }
                onClick={onApprove}
                data-testid={`contractor-document-approve-${doc.id}`}
              >
                Approve
              </Button>
            ) : null}
            {doc.status !== 'REJECTED' ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<CancelIcon />}
                disabled={reviewDisabled}
                onClick={onReject}
                data-testid={`contractor-document-reject-${doc.id}`}
              >
                Reject
              </Button>
            ) : null}
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={reviewDisabled}
              onClick={onRequestReupload}
              data-testid={`contractor-document-reupload-${doc.id}`}
            >
              Request re-upload
            </Button>
          </>
        ) : null}
      </Stack>);
  return tableRow ? (
    <tr data-testid={`contractor-document-row-${doc.id}`}>
      <td>{DOCUMENT_TYPE_LABELS[doc.type]}</td><td>{details}</td><td>{actions}</td>
    </tr>
  ) : (
    <Stack data-testid={`contractor-document-row-${doc.id}`} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1} sx={{ py: 1 }}>
      {details}{actions}
    </Stack>
  );
}
